import { SessionService } from '../../../../store/services/session.service';
import { LeftMenuService } from '../../../../store/services/left-menu.service';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MessageService } from 'app/store/services/message.service';
import { Subject } from 'rxjs';
import { IInboxCount } from '../../../../store/models/inbox';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { filter, takeUntil } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { updateLeftMenuCountSuccess } from 'app/store/actions/meeting.action';
import { openDialog } from '../../../../store/actions/page.action';
import { InfoTipsComponent } from '../../../../dialogs/info-tips/info-tips.component';
import { Store } from '@ngrx/store';
import { IReducers } from '../../../../store/reducers';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { EventService } from '../../../../store/services/event.service';
import { IToggleLeftMenuPayload, TOGGLE_LEFT_MENU_TYPE } from '../../../../store/models/page.model';
import {
  faArrowRightArrowLeft,
  faChartSimple,
  faFileCircleQuestion,
  faHeadset,
  faPeopleArrows,
  faPercent,
  faPlusCircle,
  faRightLeft,
  faRoadBarrier,
  faTrash,
  faUser,
  faUserPen,
  faUserTag,
  faHandshake,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

interface ILeftMenuItem {
  label: string;
  class?: string;
  icon?: string;
  link?: string;
  faIcon?: IconDefinition;
  extra?: string | number;
  route?: string;
  children?: ILeftMenuItem[];
  type?: string;
  iconClickHandler?: () => void;
  activeRoute?: boolean;
  activeRouteRegex?: RegExp;
  hasExtra?: boolean;
}

@Component({
  selector: 'app-left-menu',
  templateUrl: './left-menu.component.html',
  styleUrls: ['./left-menu.component.scss'],
})
export class LeftMenuComponent implements OnInit, OnDestroy {

  @ViewChild('element') element: ElementRef<HTMLElement>;
  menuItems: ILeftMenuItem[] = [];
  menuOpened: boolean;
  expanded = 'inbox';
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private allowedInboxStatusRoutes = [
    'messages',
    'meetings',
    'home',
    'campaigns',
    'contents',
    'user_contacts',
  ];

  private isFirstClick: boolean;

  constructor(
    private leftMenuService: LeftMenuService,
    private sessionService: SessionService,
    private meetingsService: MessageService,
    private actions: Actions,
    private readonly store: Store<IReducers>,
    private readonly router: Router,
    private readonly eventService: EventService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
  }

  @HostListener('document:click', ['$event'])
  clickout(event: PointerEvent) {
    const leftMenuContaner = window.document.querySelector('app-left-menu');
    const menuBoundries = leftMenuContaner.getClientRects();
    if (event.clientX > menuBoundries.item(0).width) {
      this.isFirstClick = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  shouldShrink() {
    if (!this.expanded) {
      return false;
    }

    if (!this.menuItems || !this.menuItems.length) {
      return false;
    }

    return (
      this.menuItems.filter(
        item => item.class === this.expanded && !item.children?.length,
      ).length > 0
    );
  }

  onInfoImageClick(type: string) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type,
          },
        },
      }),
    );
  }

  deactivateRoute(item: ILeftMenuItem) {
    item.activeRoute = false;

    if (item.children) {
      for (const child of item.children) {
        this.deactivateRoute(child);
      }
    }
  }

  activateRoute(item: ILeftMenuItem, segment: string) {
    if (!item.activeRoute && segment === item.route) {
      item.activeRoute = true;
    }

    if (item.children) {
      for (const child of item.children) {
        this.activateRoute(child, segment);
      }
    }
  }

  shouldShowActiveStatus(): boolean {
    for (const route of this.allowedInboxStatusRoutes) {
      if (window.location.pathname.search(new RegExp(route, 'gi')) > -1) {
        return true;
      }
    }

    return false;
  }

  ngOnInit() {
    this.eventService.on<IToggleLeftMenuPayload>(TOGGLE_LEFT_MENU_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        this.menuOpened = payload.opened;
        if (payload.opened) {
          this.element.nativeElement.style.display = 'block';
        } else {
          this.element.nativeElement.style.display = 'none';
        }
      });
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.menuItems.forEach(item => this.deactivateRoute(item));
        this.menuItems.forEach(item =>
          this.activateRoute(item, window.location.pathname),
        );
        this.isFirstClick = false;
      });
    this.leftMenuService
      .onExpand()
      .pipe(takeUntil(this.destroy$))
      .subscribe(target => {
        this.expanded = target;
        this.isFirstClick = false;
      });

    this.sessionService
      .onChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe((session: IUserSessionData) => {
        let menuItems: ILeftMenuItem[] = [];
        let inboxItems: ILeftMenuItem[] = [];
        if (session.user_type !== UserType.VSM) {
          inboxItems.push({
            label: 'Opportunities',
            icon: 'Yellow Plane',
            route: '/meetings/opportunities',
            type: 'opportunities',
            iconClickHandler: () => this.onInfoImageClick('opportunities'),
            hasExtra: true
          });
          inboxItems.push({
            label: 'Deleted Meetings',
            icon: 'Yellow Plane',
            route: '/meetings/deleted',
            type: 'deleted_meetings',
            iconClickHandler: () => this.onInfoImageClick('deleted_meetings'),
            hasExtra: true
          });

          inboxItems = [
            ...inboxItems,
            {
              label: 'Follow Up',
              icon: 'Yellow Plane',
              route: '/messages/follow_up',
              type: 'follow_up',
              iconClickHandler: () => this.onInfoImageClick('follow_up_2'),
              hasExtra: true
            },
            {
              label: 'Cycle',
              icon: 'Yellow Plane',
              route: '/messages/cycle',
              type: 'cycle',
              iconClickHandler: () => this.onInfoImageClick('cycle_2'),
              hasExtra: true
            },
            {
              label: 'Inactive',
              icon: 'Yellow Plane',
              route: '/messages/inactive',
              type: 'inactive',
              iconClickHandler: () => this.onInfoImageClick('inactive_2'),
              hasExtra: true
            },
            {
              label: 'Remove',
              icon: 'Yellow Plane',
              route: '/messages/remove',
              type: 'remove',
              iconClickHandler: () => this.onInfoImageClick('remove_2'),
              hasExtra: true
            },
          ];

          inboxItems.push({
            label: 'Spam',
            icon: 'Yellow Plane',
            route: '/messages/spam',
            type: 'spam',
            iconClickHandler: () => this.onInfoImageClick('spam'),
            hasExtra: true
          });

          if (session.c2recruit) {
            inboxItems = [
              ...inboxItems,
              {
                label: 'Available',
                icon: 'Yellow Plane',
                route: '/messages/available',
                type: 'available',
                hasExtra: true,
              },
              {
                label: 'Available - In process',
                icon: 'Yellow Plane',
                route: '/messages/available_in_progress',
                type: 'available_progress',
                hasExtra: true,
              },
              {
                label: 'Available - Placed',
                icon: 'Yellow Plane',
                route: '/messages/available_placed',
                type: 'available_placed',
                hasExtra: true,
              },
              {
                label: 'Available - Delete',
                icon: 'Yellow Plane',
                route: '/messages/available_deleted',
                type: 'available_delete',
                hasExtra: true,
              },
              {
                label: 'Future Availability',
                icon: 'Yellow Plane',
                route: '/messages/available_future',
                type: 'available_future',
                hasExtra: true,
              },
              {
                label: 'Not Available',
                icon: 'Yellow Plane',
                route: '/messages/unavailable',
                type: 'not_available',
                hasExtra: true,
              },
            ];
          }
          if (session.vip_mode) {
            inboxItems.push({
              label: 'VIP',
              icon: 'Yellow Plane',
              route: '/messages/vip',
              type: 'vip',
              iconClickHandler: () => this.onInfoImageClick('vip'),
              hasExtra: true
            });
          }
          // check if logged account is generated by and its master
          if (session.user_type === UserType.MANAGER && session.vsm) {
            inboxItems.push({
              label: 'VSM',
              icon: 'Yellow Plane',
              route: '/messages/vsm',
              type: 'vsm',
              hasExtra: true
            });
          }
          // Ok Got it new lable at the end of inbox
          inboxItems.push({
            label: 'Other',
            icon: 'Yellow Plane',
            route: '/messages/no_action',
            type: 'ok_got_it',
            iconClickHandler: () => this.onInfoImageClick('ok_got_it_2'),
            hasExtra: true
          });
        }

        menuItems.push({
          label: 'Inbox',
          class: 'inbox',
          route: '/messages',
          icon: 'Inbox Icon',
          children: inboxItems,
        });

        if (session.user_type !== UserType.VSM) {
          let contactsItems = [];

          if (
            session.user_type !== UserType.MANAGER &&
            session.user_type !== UserType.PARTNER &&
            session.user_type !== UserType.FRANCHISE &&
            session.user_type !== UserType.MASTER
          ) {
            contactsItems = [
              {
                label: 'Upload Contacts',
                route: '/user_contacts/add_contact',
                icon: 'Dog Icon',
              },
              {
                label: 'Add Contact',
                route: '/user_contacts/new',
                icon: 'Dog Icon',
              },
            ];
          }
          if (session.user_type === UserType.MASTER) {
            contactsItems.push({
              label: 'Move contacts',
              route: '/user_contacts/move',
              icon: 'Dog Icon',
            });
          }

          // hide, because of missing functionality
          // if (
          //      session.user_type !== UserType.MANAGER &&
          //      session.user_type !== UserType.PARTNER &&
          //      session.user_type !== UserType.MASTER
          // ) {
          //     contactsItems.push({
          //         label: 'Purchase Credits',
          //         route: '/contacts/purchese_creedits'
          //     });
          // }

          menuItems.push({
            label: 'Contacts',
            class: 'contacts',
            route: '/user_contacts/user_groups',
            children: contactsItems,
            icon: 'Contacts Icon',
          });
        }


        /**
         * Removed for ALL ROLES, because we do not have yea reports page
         */
        // if (
        //   session.user_type === UserType.MANAGER ||
        //   session.user_type === UserType.PARTNER
        // ) {
        //   menuItems.push({
        //     label: 'Reports',
        //     route: '/reports',
        //   });
        // }
        if (session.user_type === UserType.MANAGER) {
          menuItems.push({
            label: 'Manage',
            class: 'manage',
            route: '/accounts/view_representatives',
            children: [
              { label: 'View rep\'s', route: '/accounts/view_representatives' },
              { label: 'Manage Domains', route: '/lock_domains' },
              { label: 'Create Account', route: '/accounts/new' },
            ],
            faIcon: faUserPen,
          });
        }

        let contentItems = [];

        if (session.user_type !== UserType.VSM) {
          contentItems = [
            {
              label: 'Content Group',
              route: '/contents/content_group',
              icon: 'Dog Icon',
            },
          ];

          if (
            session.user_type !== UserType.MANAGER &&
            session.user_type !== UserType.PARTNER &&
            session.user_type !== UserType.FRANCHISE
          ) {
            if (session.user_type !== UserType.MASTER || session.user_type === UserType.MASTER && session.id === 1) {
              contentItems.push({
                label: 'Response Group',
                route: '/contents/user_replies',
                icon: 'Dog Icon',
              });
            }
          }
          contentItems = [
            ...contentItems,
            {
              label: 'Signature',
              route: '/accounts/set_signature',
              icon: 'Dog Icon',
            },
          ];
          if (
            session.user_type !== UserType.MANAGER &&
            session.user_type !== UserType.PARTNER &&
            session.user_type !== UserType.FRANCHISE
          ) {
            if (session.user_type !== UserType.MASTER || session.user_type === UserType.MASTER && session.id === 1) {
              contentItems.push({
                label: 'Quick Response',
                route: '/contents/quick_responses',
                icon: 'Dog Icon',
              });
            }
          }

          if (session.user_type === UserType.MASTER) {
            contentItems.push({
              label: 'Add Content Code',
              route: '/contents/create_master_code',
              icon: 'Dog Icon',
            });
          }

          const campaignItems = [];
          if (
            session.user_type !== UserType.MANAGER &&
            session.user_type !== UserType.PARTNER &&
            session.user_type !== UserType.FRANCHISE &&
            session.user_type !== UserType.MASTER
          ) {
            campaignItems.push({
              label: 'New Campaign',
              route: '/campaigns/new',
              icon: 'Dog Icon',
            });
          }

          if (session.user_type !== UserType.PARTNER && session.user_type !== UserType.FRANCHISE) {
            campaignItems.push({
              label: 'Campaign Block Dates',
              route: '/campaigns/block_dates',
              icon: 'Dog Icon',
            });
          }

          menuItems.push({
            label: 'Campaigns',
            class: 'campaigns',
            route: '/campaigns',
            children: campaignItems,
            icon: 'Campaigns Icon',
          });
        }

        if (session.user_type !== UserType.PARTNER && session.user_type !== UserType.FRANCHISE) {
          menuItems.push({
            label: 'Content',
            class: 'content',
            route: '/contents',
            children: contentItems,
            icon: 'Content Icon',
          });
        }

        if(session.user_type === UserType.MASTER || session.user_type === UserType.FRANCHISE) {
          let franchiseItems: ILeftMenuItem[] = [];

          if(session.user_type === UserType.MASTER) {
              franchiseItems.push({
              label: 'Franchisees',
              route: '/franchisees',
              faIcon: faRoadBarrier,
              class: 'franchisees'
            });
          }

          franchiseItems = franchiseItems.concat([
            {
              label: 'Referral partners',
              route: '/franchisees/referrals',
              faIcon: faHeadset,
              class: 'referral_partnersAdminGuard'
            },
            {
              label: 'Deals',
              route: '/franchisees/deals',
              faIcon: faHandshake,
              class: 'deals'
            },
            {
              label: 'Revenue ledger',
              route: '/franchisees/revenue_ledger',
              faIcon: faPeopleArrows,
              class: 'revenue_ledger'
            },
          ]);

          menuItems.push({
            label: 'Franchise',
            class: 'franchisees',
            route: session.user_type === UserType.MASTER ? '/franchisees' : '/franchisees/referrals',
            children: franchiseItems,
            faIcon: faUserTag,
          });
        }

        if (
          session.user_type === UserType.MASTER ||
          session.user_type === UserType.PARTNER ||
          session.user_type === UserType.FRANCHISE
        ) {
          menuItems.push({
            label: 'Accounts',
            class: 'accounts',
            route: '/accounts',
            faIcon: faUser,
          });

          if (session.user_type === UserType.PARTNER) {
            menuItems.push({
              label: 'Manage',
              class: 'manage',
              route: '/lock_domains',
              children: [{ label: 'Manage Domains', route: '/lock_domains' }],
              faIcon: faUserPen,
            });
          }

          // if (session.user_type === UserType.PARTNER) {
          //     menuItems.push({
          //         label: 'Reports',
          //         route: '/show_graph_reports'
          //     });
          // }

          if (session.user_type === UserType.MASTER) {
            const trashItems: ILeftMenuItem[] = [
              {
                label: 'Meetings',
                // icon: '/assets/images/click_popup.png',
                type: 'trash_meetings',
              },
              {
                label: 'Follow Up',
                // icon: '/assets/images/click_popup.png',
                type: 'trash_follow_up',
              },
              {
                label: 'Cycle',
                // icon: '/assets/images/click_popup.png',
                type: 'trash_cycle',
              },
              {
                label: 'Inactive',
                // icon: '/assets/images/click_popup.png',
                type: 'trash_inactive',
              },
              {
                label: 'Remove',
                // icon: '/assets/images/click_popup.png',
                type: 'trash_remove',
              },
              {
                label: 'Keep Current Status',
                // icon: '/assets/images/click_popup.png',
                type: 'trash_ok_got_it',
              },
            ];

            menuItems.push({
              label: 'Trash',
              class: 'trash',
              route: '/trashes',
              // children: trashItems,
              faIcon: faTrash,
            });

            menuItems.push({
              label: 'Discounts',
              class: 'discounts',
              route: '/discounts',
              faIcon: faPercent,
            });

            menuItems.push({
              label: 'Integrations',
              class: 'integrations',
              route: '/integrations',
              faIcon: faArrowRightArrowLeft,
            });
          }

          

          if (session.user_type === UserType.MASTER) {
            menuItems.push({
              label: 'Create Account',
              route: '/accounts/new',
              faIcon: faPlusCircle,
            });
          }

          if (session.user_type === UserType.MASTER) {
            menuItems.push({
              label: 'Edge Groups',
              class: 'vsm_groups',
              route: '/vsm_groups',
              faIcon: faHeadset,
            });

            menuItems.push({
              label: 'Blocked Emails',
              class: 'blocked_emails',
              route: '/blocked_emails',
              faIcon: faRoadBarrier,
            });
            menuItems.push({
              label: 'Bounced Emails',
              class: 'bounced_emails',
              route: '/bounced_emails',
              faIcon: faRightLeft,
            });
            menuItems.push({
              label: 'Unknown Emails',
              class: 'unknown_emails',
              route: '/unknown_emails',
              faIcon: faFileCircleQuestion,
            });
          }

          // TODO: commented, because there are not pages for this features yet!!!
          // if (session.user_type === UserType.MASTER) {
          //     menuItems = [
          //         ...menuItems,
          //         {
          //             label: 'Move Your Contacts',
          //             route: '/accounts/move',
          //             icon: faArrowsAlt
          //         },
          //         {
          //             label: 'Assign Credits',
          //             route: '/accounts/assign_credits',
          //             icon: faTasks
          //         },
          //         {
          //             label: 'Bounced Messages',
          //             route: '/messages/bounced',
          //             icon: faPaperPlane
          //         },
          //         {
          //             label: 'Blocked Emails',
          //             route: '/blocked_emails',
          //             icon: faBan
          //         },
          //         {
          //             label: 'Future Revenue',
          //             route: '/accounts/future_revenue',
          //             icon: faChartLine
          //         },
          //         {
          //             label: 'Holiday Schedule',
          //             route: '/accounts/holiday_schedule',
          //             icon: faCalendarCheck
          //         },
          //         {
          //             label: 'Unknown Messages',
          //             route: '/messages/unknown',
          //             icon: faQuestionCircle
          //         },
          //         {
          //             label: 'Valpak Announcment',
          //             route: '/announcments/create_fresh',
          //             icon: faNewspaper
          //         },
          //         {
          //             label: 'Blocked Emails List',
          //             route: '/reject_emails',
          //             icon: faBan
          //         },
          //         {
          //             label: 'Delayed Jobs Errors',
          //             route: '/email_errors',
          //             icon: faExclamationTriangle
          //         },
          //         {
          //             label: 'Admin Dashboard',
          //             route: '/dashboard',
          //             icon: faColumns
          //         },
          //         {
          //             label: 'Undo Removed Contacts',
          //             route: '/contacts/undo_removed',
          //             icon: faUndo
          //         },
          //         {
          //             label: 'Manage COS',
          //             route: '/class_of_service',
          //             icon: faVial
          //         }
          //     ];
          // }
        }

        if (session.user_type === UserType.MASTER) {
          menuItems.push({
            label: 'Closer and Contact reports',
            route: '/reports',
            faIcon: faChartSimple,
          });
          menuItems.push({
            label: 'Mail Tracking report',
            route: '/reports_mail_tracking',
            faIcon: faRightLeft,
          })
          menuItems.push({
            label: 'CoS',
            route: '/class_of_services',
            faIcon: faUserTag,
          });
        }

        menuItems = [
          ...menuItems,
          {
            label: 'Knowledge Base',
            class: 'videos',
            route: 'https://www.retreva.com/knowledge-base/',
            icon: 'Training Videos Icon',
          },
          // TODO: Commented because there is no page for this feature yet!!!
          // {
          //     label: 'Register Referral',
          //     class: 'referral',
          //     icon: faDollarSign
          // }
        ];

        if (session.user_type !== UserType.VSM && false) {
          menuItems.push({
            label: 'Register Referral',
            faIcon: faPeopleArrows,
            route: 'https://click2sell.formstack.com/forms/register_retreva_referral',
          });
        }

        this.menuItems = menuItems;

        this.menuItems.forEach(item =>
          this.activateRoute(item, window.location.pathname),
        );


        this.setInboxCount(session);
        
        this.actions
          .pipe(ofType(updateLeftMenuCountSuccess))
          .subscribe((data: any) => {
            Object.keys(data.response).forEach((key: string) => {
              this.setMenuItemsExtra(key, data.response[key]);
            });
          });
      });
  }

  setInboxCount(session: IUserSessionData) {
    if(!session || session.user_type === UserType.VSM) {
      return;
    }

    this.meetingsService
      .getInboxCount()
      .subscribe((inboxCount: IInboxCount) => {
        Object.keys(inboxCount).forEach((key: string) => {
          this.setMenuItemsExtra(key, inboxCount[key]);
        });
      });
  }

  setMenuItemsExtra(type: string, extra: number) {
    this.menuItems = this.updateExtra(this.menuItems, type, extra);
  }

  updateExtra(menuItems: ILeftMenuItem[], type: string, extra: number) {
    menuItems.map((item: ILeftMenuItem) => {
      if (item.children && item.children.length > 0) {
        item.children = this.updateExtra(item.children, type, extra);
      } else {
        if (item.type === type) {
          item.extra = extra;
        }
      }

      return item;
    });

    return menuItems;
  }

  onIconClick(event: MouseEvent, subItem: ILeftMenuItem) {
    if (subItem.iconClickHandler) {
      event.stopPropagation();
      event.preventDefault();
      subItem.iconClickHandler();
    }
  }

  getFaIconClass(item: ILeftMenuItem): { [key: string]: boolean } {
    return {
      [item.faIcon.iconName]: true,
    };
  }

  menuClicked($event) {
    if (!this.isFirstClick) {
      this.isFirstClick = true;
      $event.preventDefault();
    }
  }

  onMenuItemClick(item: ILeftMenuItem, $event: MouseEvent) {
    if (!item.children?.length) {
      $event.stopPropagation();
      return;
    }

    if (item.children?.length) {
      const wasExpanded = this.expanded === item.class;
      this.expanded = wasExpanded ? '' : item.class;
      this.leftMenuService.expand(this.expanded);
      $event.preventDefault();
      $event.stopPropagation();
      if (!wasExpanded && item.children.length === 1 && item.children[0].route) {
        this.router.navigateByUrl(item.children[0].route);
      }
    }
  }

}
